import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { parseCurrencyType, type CurrencyType } from '@/features/hierarchy/hierarchy'

export type SearchHierarchyNewRequest = {
  filter?: string | string[]
  page?: number
  noPlayer?: boolean
  onlyOneLevel?: boolean
  parentId?: string
  accessLevel?: string
}

export type SearchHierarchyNewRow = {
  username: string
}

export type SearchHierarchyNewResponse = {
  data: { data: SearchHierarchyNewRow[] }
  value: boolean
}

export async function searchHierarchyNew(
  body: SearchHierarchyNewRequest,
): Promise<SearchHierarchyNewRow[]> {
  const res = await cmApi.searchHierarchyNew(authHeaders(), body)
  if (!res.value) return []
  return res.data?.data ?? []
}

export function searchHierarchyNewUsernames(rows: SearchHierarchyNewRow[]): string[] {
  return rows.map((row) => row.username).filter(Boolean)
}

export type GetAllTmRow = {
  _id: string
  username: string
}

export type GetAllTmResponse = {
  data: GetAllTmRow[]
  value: boolean
}

export function rowsFromGetAllTm(response: GetAllTmResponse): GetAllTmRow[] {
  if (!response.value) return []
  return (response.data ?? []).filter((row) => Boolean(row._id && row.username))
}

export async function getAllTm(name = ''): Promise<GetAllTmRow[]> {
  const response = await cmApi.getAllTm(authHeaders(), { name })
  return rowsFromGetAllTm(response)
}

/** Lab / Agent-API `Member/getSiteUrl` row — `{ _id: siteUrl, name }`. */
export type GetSiteUrlRow = {
  _id: string
  name: string
}

export type GetSiteUrlResponse = {
  data: GetSiteUrlRow[]
  value: boolean
}

export function rowsFromGetSiteUrl(response: GetSiteUrlResponse): GetSiteUrlRow[] {
  if (!response.value) return []
  return (response.data ?? []).filter((row) => Boolean(row._id))
}

/** Distinct TM white-label domains for Card Game domain dropdown. */
export async function getSiteUrl(): Promise<GetSiteUrlRow[]> {
  const response = await cmApi.getSiteUrl(authHeaders(), {})
  return rowsFromGetSiteUrl(response)
}

export type MemberBalance = {
  balance: number
  available: number
  creditLimit: number
  distributed: number
  exposure: number
  balanceUp: number
  currency: CurrencyType
}

type GetMemberBalanceResponse = {
  value: boolean
  data?: {
    balanceMinor?: string
    availableMinor?: string
    creditLimitMinor?: string
    distributedMinor?: string
    exposureMinor?: string
    balanceUpMinor?: string
    currencyType?: string
    balance?: number
    available?: number
    creditLimit?: number
    distributed?: number
    exposure?: number
    balanceUp?: number
  }
}

const fromMinorSigned = (raw: string | undefined): number => {
  const value = Number(raw ?? 0)
  return Number.isFinite(value) ? value / 100 : 0
}

export function memberBalanceFromApi(response: GetMemberBalanceResponse): MemberBalance | null {
  if (!response.value || !response.data) return null
  const d = response.data
  if (typeof d.balance === 'number' || typeof d.available === 'number') {
    return {
      balance: d.balance ?? 0,
      available: d.available ?? 0,
      creditLimit: d.creditLimit ?? 0,
      distributed: d.distributed ?? 0,
      exposure: d.exposure ?? 0,
      balanceUp: d.balanceUp ?? 0,
      currency: parseCurrencyType(d.currencyType),
    }
  }
  return {
    balance: fromMinorSigned(d.balanceMinor),
    available: fromMinorSigned(d.availableMinor),
    creditLimit: fromMinorSigned(d.creditLimitMinor),
    distributed: fromMinorSigned(d.distributedMinor),
    exposure: fromMinorSigned(d.exposureMinor),
    balanceUp: fromMinorSigned(d.balanceUpMinor),
    currency: parseCurrencyType(d.currencyType),
  }
}

export async function getMemberBalance(): Promise<MemberBalance | null> {
  const response = await cmApi.getMemberBalance(authHeaders(), {})
  return memberBalanceFromApi(response)
}
